// src/components/wells/WellAiSummaryCard.js
import React from 'react';
// --- UPDATE: memoisation hooks
import { memo, useMemo } from 'react';
// --- END UPDATE
import {
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  HStack,
  Badge,
  Icon,
  Box,
  Flex,
  Text,
  useColorModeValue,
  // --- UPDATE
  Tooltip,
  CircularProgress,
  Skeleton,
  SkeletonText,
  Wrap,
  WrapItem,
  Button,
  // --- END UPDATE
} from '@chakra-ui/react';
import { MdShield, MdAutoAwesome, MdWarning } from 'react-icons/md';
// --- UPDATE
import { MdGrain, MdTimer, MdTrendingUp, MdUpdate } from 'react-icons/md';
import {
  getAiForWell,
  extractAiMetrics,
  formatMinutesShort,
  formatRelativeTime,
  getRainfallRisk,
  aiCriticalPulse,
  STALE_PREDICTION_MS,
  usePrefersReducedMotion,
} from './WellAiStatusCell';
// --- END UPDATE

export const WellAiSummaryCard = ({
  wells = [],
  aiPredictions = {},
  subtextColor,
  // --- UPDATE: optional props (backwards compatible)
  isLoading = false,
  weather = null,
  onSelectWell,
  staleAfterMs = STALE_PREDICTION_MS,
  // --- END UPDATE
}) => {
  const cardBorder = useColorModeValue('purple.200', 'purple.800');
  const cardBg = useColorModeValue('rgba(159, 122, 234, 0.05)', 'rgba(159, 122, 234, 0.12)');
  // --- UPDATE: hoisted colour tokens
  const prefersReducedMotion = usePrefersReducedMotion();
  const insightBg = useColorModeValue('purple.50', 'rgba(159, 122, 234, 0.12)');
  const insightBorder = useColorModeValue('purple.100', 'purple.700');
  const trackBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const chipHoverBg = useColorModeValue('purple.100', 'whiteAlpha.200');
  const rainColor = useColorModeValue('blue.600', 'blue.200');
  // --- END UPDATE

  // Compute aggregate AI metrics across all loaded wells
  let highRiskCount = 0;
  let criticalCount = 0;
  let averageRul = 0;
  let evaluatedWells = 0;
  let rulCount = 0; // --- UPDATE (wells v1)

  wells.forEach((well) => {
    const ai = aiPredictions[String(well.id)] || well.ai_predictions;
    if (ai) {
      evaluatedWells++;
      const prob = ai.floodRiskProbability ?? ai.flood_probability ?? 0;
      const severity = (ai.predictedSeverity || ai.flood_risk || '').toUpperCase();
      // --- UPDATE (wells v1): only real RUL values count towards the average (missing was counted as 0)
      const rul = Number(ai.predicted_rul_days ?? ai.rul);
      // --- END UPDATE

      if (severity === 'CRITICAL' || prob >= 0.85) {
        criticalCount++;
      } else if (severity === 'HIGH' || severity === 'WARNING' || prob >= 0.60) {
        highRiskCount++;
      }
      if (Number.isFinite(rul)) { averageRul += rul; rulCount++; }
    }
  });

  // --- UPDATE (wells v1): null (shown as —) instead of an invented 365 days
  const avgRulDays = rulCount > 0 ? Math.round(averageRul / rulCount) : null;
  const totalAtRisk = criticalCount + highRiskCount;

  // --- UPDATE: extended fleet insights (memoised; the headline numbers above are untouched)
  const insights = useMemo(() => {
    const list = Array.isArray(wells) ? wells : [];
    const ranked = [];
    let watch = 0;
    let stable = 0;
    let confSum = 0;
    let confN = 0;
    let healthSum = 0;
    let healthN = 0; // --- UPDATE (wells v1)
    let rising = 0;
    let latestUpdate = null;
    let earliest = null;

    list.forEach((well) => {
      const ai = getAiForWell(well, aiPredictions);
      if (!ai) return;
      const m = extractAiMetrics(ai);

      // Same thresholds as the headline loop, so the distribution bar adds up to the headline number
      const isCrit = m.severity === 'CRITICAL' || m.probability >= 0.85;
      const isHigh = !isCrit && (m.severity === 'HIGH' || m.severity === 'WARNING' || m.probability >= 0.6);
      if (!isCrit && !isHigh) {
        if (m.probability >= 0.4) watch += 1;
        else stable += 1;
      }

      if (m.confidence !== null) {
        confSum += m.confidence;
        confN += 1;
      }
      if (m.healthScore !== null) { healthSum += m.healthScore; healthN += 1; } // --- UPDATE (wells v1)
      if (m.trend === 'rising') rising += 1;
      if (m.updatedAt !== null && (latestUpdate === null || m.updatedAt > latestUpdate)) {
        latestUpdate = m.updatedAt;
      }
      if (m.estMinutes !== null && (earliest === null || m.estMinutes < earliest.m.estMinutes)) {
        earliest = { well, m };
      }
      ranked.push({ well, m, isCrit, isHigh });
    });

    const top = ranked
      .filter((r) => r.isCrit || r.isHigh || r.m.probability >= 0.4)
      .sort((a, b) => Number(b.isCrit) - Number(a.isCrit) || b.m.probability - a.m.probability)
      .slice(0, 3);

    return {
      evaluated: ranked.length,
      total: list.length,
      watch,
      stable,
      top,
      earliest,
      rising,
      latestUpdate,
      avgConfidence: confN > 0 ? confSum / confN : null,
      avgHealth: healthN > 0 ? Math.round(healthSum / healthN) : null,
      coveragePct: list.length > 0 ? Math.round((ranked.length / list.length) * 100) : 0,
    };
  }, [wells, aiPredictions]);

  const rain24 =
    weather && Number.isFinite(Number(weather.next24hRainMm)) ? Number(weather.next24hRainMm) : null;
  const rainRisk = getRainfallRisk(rain24);
  const isStale = insights.latestUpdate !== null && Date.now() - insights.latestUpdate > staleAfterMs;

  const narrative = useMemo(() => {
    const earliestName = insights.earliest
      ? insights.earliest.well.name || `Well ${insights.earliest.well.id}`
      : null;
    if (criticalCount > 0) {
      return `${criticalCount} well(s) predicted to overtop${
        insights.earliest ? ` — earliest ${earliestName} in ${formatMinutesShort(insights.earliest.m.estMinutes)}` : ''
      }. Crew dispatch recommended.`;
    }
    if (totalAtRisk > 0) {
      return `${totalAtRisk} well(s) trending toward spill threshold${
        rain24 !== null && rain24 >= 10 ? ` — ${rain24} mm rain forecast will amplify risk` : ''
      }.`;
    }
    if (rain24 !== null && rain24 >= 25) {
      return `Fleet stable, but ${rain24} mm rain is forecast in 24h — watch low-clearance wells.`;
    }
    if (insights.rising > 0) {
      return `Fleet stable. ${insights.rising} well(s) show a rising risk trend — keep monitoring.`;
    }
    if (insights.evaluated > 0) {
      return insights.avgHealth !== null
        ? `Fleet stable. Avg asset health ${insights.avgHealth}% across ${insights.evaluated} evaluated well(s).`
        : `Fleet stable across ${insights.evaluated} evaluated well(s).`;
    }
    return 'Awaiting first ML evaluation cycle for the well register.';
  }, [criticalCount, totalAtRisk, rain24, insights]);

  const segments = [
    { key: 'critical', label: 'Critical', count: criticalCount, color: 'red.500' },
    { key: 'elevated', label: 'Elevated', count: highRiskCount, color: 'orange.400' },
    { key: 'watch', label: 'Watch', count: insights.watch, color: 'yellow.400' },
    { key: 'stable', label: 'Stable', count: insights.stable, color: 'green.400' },
  ];
  const segmentTotal = Math.max(1, insights.evaluated);
  // --- END UPDATE

  return (
    <Stat>
      <Flex justify="space-between" align="start">
        <StatLabel fontSize="xs" fontWeight="700" color="purple.500" display="flex" alignItems="center" gap={1}>
          <Icon as={MdAutoAwesome} boxSize={3.5} />
          AI Flood & Health Risk
        </StatLabel>
        {criticalCount > 0 ? (
          <Badge
            colorScheme="red"
            variant="solid"
            fontSize="9px"
            borderRadius="full"
            px={1.5}
            className="animate-pulse"
            // --- UPDATE: real pulse (no Tailwind dependency), respects reduced motion
            animation={prefersReducedMotion ? undefined : `${aiCriticalPulse} 1.8s ease-out infinite`}
            // --- END UPDATE
          >
            SURGE CRITICAL
          </Badge>
        ) : (
          <Badge colorScheme="purple" variant="subtle" fontSize="9px" borderRadius="full" px={1.5}>
            LIVE ML
          </Badge>
        )}
      </Flex>
      <StatNumber fontSize="2xl" fontWeight="800" color={totalAtRisk > 0 ? (criticalCount > 0 ? 'red.500' : 'orange.400') : 'purple.500'}>
        {totalAtRisk}
      </StatNumber>
      <StatHelpText fontSize="xs" mb={0} color={subtextColor}>
        {totalAtRisk > 0 ? `${totalAtRisk} well(s) near spill threshold` : avgRulDays !== null ? `Avg RUL: ~${avgRulDays} days` : `${evaluatedWells} well(s) evaluated`}
      </StatHelpText>

      {/* --- UPDATE: AI fleet intelligence block */}
      {isLoading && evaluatedWells === 0 ? (
        <Box mt={2}>
          <Skeleton h="6px" borderRadius="full" mb={2} />
          <SkeletonText noOfLines={2} spacing={1.5} skeletonHeight="2" />
        </Box>
      ) : (
        <Box mt={2}>
          {/* Risk distribution bar */}
          <Tooltip
            hasArrow
            label={segments.map((s) => `${s.label} ${s.count}`).join(' · ')}
            openDelay={150}
          >
            <Flex
              h="6px"
              borderRadius="full"
              overflow="hidden"
              bg={trackBg}
              role="img"
              aria-label={`AI risk distribution: ${segments.map((s) => `${s.count} ${s.label}`).join(', ')}`}
            >
              {segments.map(
                (s) =>
                  s.count > 0 && (
                    <Box
                      key={s.key}
                      w={`${(s.count / segmentTotal) * 100}%`}
                      bg={s.color}
                      transition="width 0.4s ease"
                    />
                  )
              )}
            </Flex>
          </Tooltip>

          {/* Model meta: confidence · coverage · freshness */}
          <HStack spacing={2} mt={1.5} fontSize="9px" color={subtextColor} flexWrap="wrap">
            {insights.avgConfidence !== null && (
              <Tooltip hasArrow label="Average model confidence across evaluated wells">
                <HStack spacing={1}>
                  <CircularProgress
                    value={Math.round(insights.avgConfidence * 100)}
                    size="12px"
                    thickness="16px"
                    color="purple.400"
                  />
                  <Text fontWeight="700">{Math.round(insights.avgConfidence * 100)}% conf</Text>
                </HStack>
              </Tooltip>
            )}
            <Tooltip hasArrow label={`${insights.coveragePct}% of wells have an ML prediction`}>
              <Text fontWeight="700">
                ML {insights.evaluated}/{insights.total}
              </Text>
            </Tooltip>
            {insights.latestUpdate !== null && (
              <HStack spacing={0.5} color={isStale ? 'orange.400' : undefined}>
                <Icon as={MdUpdate} boxSize={2.5} />
                <Text fontWeight="600">{formatRelativeTime(insights.latestUpdate)}</Text>
              </HStack>
            )}
            {insights.rising > 0 && (
              <HStack spacing={0.5} color="red.400">
                <Icon as={MdTrendingUp} boxSize={2.5} />
                <Text fontWeight="700">{insights.rising} rising</Text>
              </HStack>
            )}
          </HStack>

          {/* Earliest predicted overtopping */}
          {insights.earliest && (
            <HStack spacing={1} mt={1} fontSize="9px" color={criticalCount > 0 ? 'red.500' : subtextColor}>
              <Icon as={MdTimer} boxSize={3} />
              <Text fontWeight="700" noOfLines={1}>
                Earliest spill {formatMinutesShort(insights.earliest.m.estMinutes)} ·{' '}
                {insights.earliest.well.name || `Well ${insights.earliest.well.id}`}
              </Text>
            </HStack>
          )}

          {/* Weather coupling */}
          {rain24 !== null && (
            <HStack spacing={1} mt={1} fontSize="9px" color={rainColor}>
              <Icon as={MdGrain} boxSize={3} />
              <Text fontWeight="700">
                {rain24} mm/24h
                {Number.isFinite(Number(weather?.peakPrecipProbability))
                  ? ` · ${weather.peakPrecipProbability}% peak`
                  : ''}
              </Text>
              <Badge colorScheme={rainRisk.scheme} fontSize="8px" variant="subtle" borderRadius="full">
                {rainRisk.level}
              </Badge>
            </HStack>
          )}

          {/* Top at-risk wells */}
          {insights.top.length > 0 && (
            <Wrap spacing={1} mt={1.5}>
              {insights.top.map(({ well, m, isCrit }) => {
                const label = `${well.name || `Well ${well.id}`} · ${m.probabilityPct}%`;
                return (
                  <WrapItem key={String(well.id)}>
                    {onSelectWell ? (
                      <Button
                        size="xs"
                        h="18px"
                        px={1.5}
                        fontSize="9px"
                        variant="outline"
                        colorScheme={isCrit ? 'red' : 'orange'}
                        borderRadius="full"
                        _hover={{ bg: chipHoverBg }}
                        onClick={() => onSelectWell(well)}
                        aria-label={`Select ${label}`}
                      >
                        {label}
                      </Button>
                    ) : (
                      <Badge colorScheme={isCrit ? 'red' : 'orange'} fontSize="8px" borderRadius="full">
                        {label}
                      </Badge>
                    )}
                  </WrapItem>
                );
              })}
            </Wrap>
          )}

          {/* AI narrative */}
          <Tooltip hasArrow label={narrative} openDelay={300}>
            <HStack
              align="start"
              spacing={1}
              mt={1.5}
              p={1.5}
              bg={insightBg}
              borderWidth="1px"
              borderColor={insightBorder}
              borderRadius="md"
            >
              <Icon as={MdAutoAwesome} boxSize={3} color="purple.400" mt="1px" flexShrink={0} />
              <Text fontSize="9px" lineHeight="1.4" noOfLines={2}>
                {narrative}
              </Text>
            </HStack>
          </Tooltip>
        </Box>
      )}
      {/* --- END UPDATE */}
    </Stat>
  );
};

// --- UPDATE: memoised default export (inputs are stable props from the panel)
export default memo(WellAiSummaryCard);
// --- END UPDATE